<template>
  <div id="app-root" class="app-root" :data-theme="theme">
    <site-header :theme="theme" @set-theme="setTheme" />
    <main class="main-area">
      <router-view />
    </main>
    <footer class="site-footer">
      <p class="footer-line">—— Copyright © 2002-2026 版权所有 ——</p>
      <p class="footer-icp">
        <a
          class="footer-icp-link"
          href="https://beian.miit.gov.cn/"
          target="_blank"
          rel="noopener noreferrer"
        >浙ICP备2026032392号-1</a>
      </p>
    </footer>
  </div>
</template>

<script>
import SiteHeader from '@/components/SiteHeader.vue'

const STORAGE_KEY = 'acaa-theme'

export default {
  name: 'App',
  components: { SiteHeader },
  data() {
    return {
      theme: 'light'
    }
  },
  created() {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved === 'light' || saved === 'dark') {
      this.theme = saved
    }
    this.syncThemeDom()
  },
  watch: {
    theme() {
      localStorage.setItem(STORAGE_KEY, this.theme)
      this.syncThemeDom()
    }
  },
  methods: {
    setTheme(mode) {
      if (mode !== 'light' && mode !== 'dark') return
      this.theme = mode
    },
    syncThemeDom() {
      document.documentElement.setAttribute('data-theme', this.theme)
    }
  }
}
</script>

<style lang="scss">
html {
  background: #f4f1ea;
}
html[data-theme='dark'] {
  background: #050508;
}

html,
body {
  margin: 0;
  min-height: 100%;
}

#app-root.app-root {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: 'Noto Sans SC', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  transition: background-color 0.35s ease, color 0.35s ease;
}

#app-root.app-root[data-theme='dark'] {
  background: #050508;
  color: #e8e0d4;
  --page-bg-base: #050508;
  --page-bg-result: #060608;
  --hero-vignette: rgba(0, 0, 0, 0.5);
  --grain-opacity: 0.45;
  --hero-noise-opacity: 0.6;
  --footer-line: rgba(255, 255, 255, 0.35);
  --home-script: rgba(232, 224, 212, 0.55);
  --home-tagline: rgba(232, 224, 212, 0.45);
  --search-bar-shadow: 0 8px 40px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(201, 169, 98, 0.15);
  --header-bg: linear-gradient(180deg, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.35) 100%);
  --header-border: rgba(201, 169, 98, 0.12);
  --nav-link: rgba(232, 224, 212, 0.78);
  --nav-link-hover: #f5ecd8;
  --nav-link-active: #f0e6d2;
  --brand-sub: rgba(201, 169, 98, 0.55);
  --theme-switch-track: rgba(255, 255, 255, 0.06);
  --theme-switch-border: rgba(201, 169, 98, 0.22);
  --theme-switch-muted: rgba(232, 224, 212, 0.55);
  --theme-switch-active-bg: rgba(201, 169, 98, 0.22);
  --theme-switch-active-fg: #f5ecd8;
  --about-intro: rgba(232, 224, 212, 0.72);
  --about-intro-emphasis: rgba(232, 224, 212, 0.88);
  --about-body: rgba(232, 224, 212, 0.62);
  --about-muted: rgba(232, 224, 212, 0.55);
  --about-card-bg: rgba(201, 169, 98, 0.06);
  --about-img-bg: rgba(0, 0, 0, 0.25);
  --about-h3: rgba(232, 224, 212, 0.92);
  --about-h2: rgba(247, 239, 217, 0.95);
  --about-steps: rgba(232, 224, 212, 0.65);
  --about-step-title: rgba(232, 224, 212, 0.88);
  --about-caption: rgba(201, 169, 98, 0.65);
  --about-slogan-sub: rgba(232, 224, 212, 0.55);
  --about-img-shadow: 0 12px 36px rgba(0, 0, 0, 0.35);
  --query-scan-title: rgba(255, 255, 255, 0.92);
  --query-grain-opacity: 0.45;
  --query-desc: rgba(232, 224, 212, 0.5);
  --query-btn-requery-fg: rgba(255, 255, 255, 0.75);
  --query-btn-requery-hover: #fff;
  --brand-logo-filter: brightness(1.1) contrast(1.06) drop-shadow(0 0 18px rgba(212, 175, 55, 0.45));
  --hero-logo-img-filter: drop-shadow(0 12px 40px rgba(201, 169, 98, 0.45));
  --query-report-page-bg: #2a2a2e;
}

#app-root.app-root[data-theme='light'] {
  background: #f4f1ea;
  color: #2c261c;
  --page-bg-base: #f4f1ea;
  --page-bg-result: #ebe6dc;
  --hero-vignette: rgba(160, 140, 100, 0.18);
  --grain-opacity: 0.28;
  --hero-noise-opacity: 0.35;
  --footer-line: rgba(0, 0, 0, 0.42);
  --home-script: rgba(60, 52, 38, 0.72);
  --home-tagline: rgba(60, 52, 38, 0.55);
  --search-bar-shadow: 0 10px 32px rgba(60, 48, 30, 0.1), 0 0 0 1px rgba(180, 160, 110, 0.22);
  --header-bg: linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(247, 244, 238, 0.94) 100%);
  --header-border: rgba(160, 140, 90, 0.28);
  --nav-link: rgba(55, 48, 36, 0.85);
  --nav-link-hover: #4a4030;
  --nav-link-active: #6b5420;
  --brand-sub: rgba(120, 100, 60, 0.75);
  --theme-switch-track: rgba(0, 0, 0, 0.05);
  --theme-switch-border: rgba(160, 140, 90, 0.35);
  --theme-switch-muted: rgba(55, 48, 36, 0.55);
  --theme-switch-active-bg: rgba(201, 169, 98, 0.38);
  --theme-switch-active-fg: #3d3424;
  --about-intro: rgba(44, 38, 28, 0.88);
  --about-intro-emphasis: rgba(44, 38, 28, 0.95);
  --about-body: rgba(44, 38, 28, 0.72);
  --about-muted: rgba(44, 38, 28, 0.55);
  --about-card-bg: rgba(201, 169, 98, 0.12);
  --about-img-bg: rgba(255, 255, 255, 0.65);
  --about-h3: rgba(44, 38, 28, 0.92);
  --about-h2: rgba(44, 38, 28, 0.95);
  --about-steps: rgba(44, 38, 28, 0.68);
  --about-step-title: rgba(44, 38, 28, 0.9);
  --about-caption: rgba(120, 95, 45, 0.75);
  --about-slogan-sub: rgba(44, 38, 28, 0.55);
  --about-img-shadow: 0 12px 28px rgba(60, 48, 30, 0.12);
  --query-scan-title: rgba(44, 38, 28, 0.92);
  --query-grain-opacity: 0.2;
  --query-desc: rgba(44, 38, 28, 0.55);
  --query-btn-requery-fg: rgba(44, 38, 28, 0.85);
  --query-btn-requery-hover: #1a1208;
  --brand-logo-filter: drop-shadow(0 2px 8px rgba(60, 48, 30, 0.12))
    drop-shadow(0 4px 20px rgba(201, 169, 98, 0.32));
  --hero-logo-img-filter: drop-shadow(0 6px 28px rgba(201, 169, 98, 0.28))
    drop-shadow(0 2px 10px rgba(0, 0, 0, 0.06));
  --query-report-page-bg: #e8e8e8;
}

.main-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.site-footer {
  text-align: center;
  padding: 24px 16px 32px;
  font-size: 12px;
  color: var(--footer-line);
  letter-spacing: 0.02em;
  transition: color 0.35s ease;
  .footer-line {
    margin: 0 0 8px;
  }
  .footer-icp {
    margin: 0;
  }
  .footer-icp-link {
    color: #4a90e2;
    text-decoration: none;
    transition: opacity 0.2s ease;
    &:hover {
      text-decoration: underline;
      opacity: 0.9;
    }
    &:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
    }
  }
}
</style>
